import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Checkbox,
  Radio,
  RadioGroup,
  FormLabel
} from '@mui/material';
import { Save, ArrowBack } from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { validatePhoneNumber, cleanPhoneNumber } from '../utils/validation';

const API_URL = process.env.REACT_APP_API_URL;

const ReportForm = ({ appointment, ...props }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form data
  const [patientInfo, setPatientInfo] = useState({
    name: '',
    gender: '',
    dob: '',
    phone: '',
    address: '',
    referringDoctor: '',
    registrationNumber: '',
    collectionAddress: ''
  });
  const [phoneError, setPhoneError] = useState('');

  const [groupedTests, setGroupedTests] = useState({});
  const [selectedTests, setSelectedTests] = useState([]);
  const [testResults, setTestResults] = useState({});
  const [searchTerm, setSearchTerm] = useState("");

  const [dates, setDates] = useState({
    registeredDate: '',
    collectedDate: '',
    receivedDate: '',
    reportedDate: ''
  });

  const { user } = useAuth();
  const [clinicalNotes, setClinicalNotes] = useState('');
  const [interpretation, setInterpretation] = useState('');
  const [pathologists, setPathologists] = useState([]);
  const [selectedPathologist, setSelectedPathologist] = useState('');

  useEffect(() => {
    fetchGroupedTests();
    fetchPathologists();
  }, []);

  useEffect(() => {
    if (appointment) {
      setPatientInfo({
        name: appointment.patient?.name || '',
        gender: appointment.patient?.gender || '',
        dob: appointment.patient?.dob || '',
        phone: appointment.patient?.phone || '',
        address: appointment.patient?.address || '',
        referringDoctor: appointment.patient?.referringDoctor || '',
        registrationNumber: appointment.patient?.registrationNumber || '',
        collectionAddress: appointment.patient?.address || ''
      });
      setSelectedTests(appointment.tests?.map(test => test.id) || []);
    }
  }, [appointment]);

  const fetchGroupedTests = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/tests/grouped-by-category`);
      setGroupedTests(response.data);
    } catch (err) {
      setError('Failed to load tests');
    }
  };

  const fetchPathologists = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/users/role/PATHOLOGIST`);
      setPathologists(response.data);
    } catch (err) {
      setError('Failed to load pathologists');
    }
  };

  const handleTestSelection = (testId, checked) => {
    if (checked) {
      setSelectedTests([...selectedTests, testId]);
      setTestResults({ ...testResults, [testId]: '' });
    } else {
      setSelectedTests(selectedTests.filter(id => id !== testId));
      const newResults = { ...testResults };
      delete newResults[testId];
      setTestResults(newResults);
    }
  };

  const handleTestResultChange = (testId, value) => {
    setTestResults({ ...testResults, [testId]: value });
  };

  const validatePatientInfo = () => {
    if (!patientInfo.name.trim()) return 'Patient name is required.';
    if (!patientInfo.gender) return 'Gender is required.';
    if (!patientInfo.dob) return 'Date of birth is required.';
    if (!patientInfo.phone.trim()) return 'Phone number is required.';
    
    // Validate phone number
    const phoneValidation = validatePhoneNumber(patientInfo.phone);
    if (!phoneValidation.isValid) {
      return phoneValidation.error;
    }
    
    if (!patientInfo.address.trim()) return 'Address is required.';
    if (!patientInfo.referringDoctor.trim()) return 'Referring doctor is required.';
    if (!patientInfo.registrationNumber.trim()) return 'Registration number is required.';
    return null;
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    // Frontend validation for patient info
    const validationError = validatePatientInfo();
    if (validationError) {
      setError(validationError);
      setLoading(false);
      return;
    }

    if (selectedTests.length === 0) {
      setError('Please select at least one test.');
      setLoading(false);
      return;
    }

    try {
      const testResultsArray = selectedTests.map(testId => ({
        testId: parseInt(testId),
        value: testResults[testId] || ''
      }));

      const reportData = {
        patientInfo: {
          name: patientInfo.name,
          gender: patientInfo.gender,
          dob: patientInfo.dob,
          phone: cleanPhoneNumber(patientInfo.phone),
          address: patientInfo.address,
          referringDoctor: patientInfo.referringDoctor,
          registrationNumber: patientInfo.registrationNumber,
          collectionAddress: patientInfo.collectionAddress
        },
        testResults: testResultsArray,
        registeredDate: dates.registeredDate ? new Date(dates.registeredDate).toISOString() : null,
        collectedDate: dates.collectedDate ? new Date(dates.collectedDate).toISOString() : null,
        receivedDate: dates.receivedDate ? new Date(dates.receivedDate).toISOString() : null,
        reportedDate: dates.reportedDate ? new Date(dates.reportedDate).toISOString() : null,
        clinicalNotes,
        interpretation,
        pathologistId: selectedPathologist
      };

      let response;
      if (appointment) {
        response = await axios.post(`${API_URL}/api/laboratorist/reports/submit-from-appointment?appointmentId=${appointment.id}`, reportData);
      } else {
        response = await axios.post(`${API_URL}/api/reports/submit`, reportData);
      }

      setSuccess('Report submitted successfully!');

      setTimeout(() => {
        navigate(`/report/${response.data.id}`);
      }, 2000);
    } catch (err) {
      let errorMsg = 'Failed to submit report.';
      if (err.response) {
        if (err.response.data && err.response.data.message) {
          errorMsg = `Failed to submit report: ${err.response.data.message}`;
        } else if (typeof err.response.data === 'string') {
          errorMsg = `Failed to submit report: ${err.response.data}`;
        } else if (err.response.status) {
          errorMsg = `Failed to submit report (HTTP ${err.response.status}): ${err.response.statusText}`;
        }
      } else if (err.message) {
        errorMsg = `Failed to submit report: ${err.message}`;
      }
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    // Only allow digits and common phone separators
    const cleanedValue = value.replace(/[^\d\s\-\(\)]/g, '');
    setPatientInfo({ ...patientInfo, phone: cleanedValue });
    
    // Validate phone number
    const validation = validatePhoneNumber(cleanedValue);
    if (!validation.isValid && cleanedValue.length > 0) {
      setPhoneError(validation.error);
    } else {
      setPhoneError('');
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 4, borderRadius: 3, boxShadow: '0 4px 24px rgba(42,46,91,0.08)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate(-1)}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <Typography variant="h4" component="h1" sx={{ color: '#2A2E5B', fontWeight: 700 }}>
            New Report
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Patient Information */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ color: '#2A2E5B', fontWeight: 700 }}>
              Patient Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Patient Name"
                  value={patientInfo.name}
                  onChange={(e) => setPatientInfo({ ...patientInfo, name: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Gender</InputLabel>
                  <Select
                    value={patientInfo.gender}
                    onChange={(e) => setPatientInfo({ ...patientInfo, gender: e.target.value })}
                    label="Gender"
                  >
                    <MenuItem value="MALE">Male</MenuItem>
                    <MenuItem value="FEMALE">Female</MenuItem>
                    <MenuItem value="OTHER">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Date of Birth"
                  type="date"
                  value={patientInfo.dob}
                  onChange={(e) => setPatientInfo({ ...patientInfo, dob: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={patientInfo.phone}
                  onChange={handlePhoneChange}
                  error={!!phoneError}
                  helperText={phoneError || 'Enter 10-digit phone number'}
                  required
                  inputProps={{
                    maxLength: 15,
                    placeholder: '1234567890'
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Address"
                  value={patientInfo.address}
                  onChange={(e) => setPatientInfo({ ...patientInfo, address: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Referring Doctor"
                  value={patientInfo.referringDoctor}
                  onChange={(e) => setPatientInfo({ ...patientInfo, referringDoctor: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Registration Number"
                  value={patientInfo.registrationNumber}
                  onChange={(e) => setPatientInfo({ ...patientInfo, registrationNumber: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Collection Address (optional)"
                  value={patientInfo.collectionAddress}
                  onChange={(e) => setPatientInfo({ ...patientInfo, collectionAddress: e.target.value })}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Test Selection */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ color: '#2A2E5B', fontWeight: 700 }}>
              Select Tests
            </Typography>
            <TextField
              label="Search Test"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              sx={{ mb: 2, width: '100%' }}
            />
            {Object.entries(groupedTests).map(([categoryId, tests]) => {
              const category = tests[0]?.testCategory;
              // Filter tests by search term
              const filteredTests = tests.filter(test =>
                test.name.toLowerCase().includes(searchTerm.toLowerCase())
              );
              if (filteredTests.length === 0) return null;
              return (
                <Box key={categoryId} sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {category?.name || 'Other'}
                  </Typography>
                  <Grid container spacing={2}>
                    {filteredTests.map((test) => (
                      <Grid item xs={12} sm={6} md={4} key={test.id}>
                        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 24px rgba(42,46,91,0.08)' }}>
                          <CardContent>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={selectedTests.includes(test.id)}
                                  onChange={(e) => handleTestSelection(test.id, e.target.checked)}
                                />
                              }
                              label={
                                <Box>
                                  <Typography variant="h6">{test.name}</Typography>
                                  <Typography variant="body2" color="textSecondary">
                                    Unit: {test.unit} | Reference: {test.referenceRange}
                                  </Typography>
                                </Box>
                              }
                            />
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              );
            })}
          </Grid>

          {/* Test Results */}
          {selectedTests.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ color: '#2A2E5B', fontWeight: 700 }}>
                Enter Test Results
              </Typography>
              <Grid container spacing={3}>
                {selectedTests.map((testId) => {
                  // Find the test object from all groupedTests
                  let foundTest = null;
                  for (const tests of Object.values(groupedTests)) {
                    const t = tests.find(t => t.id === parseInt(testId));
                    if (t) { foundTest = t; break; }
                  }
                  if (!foundTest) return null;
                  const test = foundTest;
                  return (
                    <Grid item xs={12} sm={6} key={testId}>
                      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 24px rgba(42,46,91,0.08)' }}>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            {test.name}
                          </Typography>
                          <Typography variant="body2" color="textSecondary" gutterBottom>
                            Unit: {test.unit} | Reference: {test.referenceRange}
                          </Typography>
                          {test.dataType === 'BOOLEAN' ? (
                            <FormControl fullWidth>
                              <InputLabel>Result</InputLabel>
                              <Select
                                value={testResults[testId] || ''}
                                onChange={(e) => handleTestResultChange(testId, e.target.value)}
                                label="Result"
                              >
                                <MenuItem value="true">Positive</MenuItem>
                                <MenuItem value="false">Negative</MenuItem>
                              </Select>
                            </FormControl>
                          ) : test.dataType === 'NUMBER' ? (
                            <TextField
                              fullWidth
                              label="Value"
                              type="number"
                              value={testResults[testId] || ''}
                              onChange={(e) => handleTestResultChange(testId, e.target.value)}
                            />
                          ) : (
                            <TextField
                              fullWidth
                              label="Value"
                              value={testResults[testId] || ''}
                              onChange={(e) => handleTestResultChange(testId, e.target.value)}
                            />
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Grid>
          )}

          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Registered On"
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
                value={dates.registeredDate}
                onChange={e => setDates({ ...dates, registeredDate: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Collected On"
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
                value={dates.collectedDate}
                onChange={e => setDates({ ...dates, collectedDate: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Received On"
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
                value={dates.receivedDate}
                onChange={e => setDates({ ...dates, receivedDate: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Reported On"
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
                value={dates.reportedDate}
                onChange={e => setDates({ ...dates, reportedDate: e.target.value })}
              />
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ color: '#2A2E5B', fontWeight: 700 }}>
              Lab Incharge & Pathologist
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Lab Incharge"
                  value={user?.name || ''}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Pathologist</InputLabel>
                  <Select
                    value={selectedPathologist}
                    label="Pathologist"
                    onChange={e => setSelectedPathologist(e.target.value)}
                  >
                    {pathologists.map((p) => (
                      <MenuItem key={p.id} value={p.id}>{p.name} ({p.designation})</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Clinical Notes"
              multiline
              minRows={2}
              value={clinicalNotes}
              onChange={e => setClinicalNotes(e.target.value)}
              sx={{ mt: 2 }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Interpretation"
              multiline
              minRows={2}
              value={interpretation}
              onChange={e => setInterpretation(e.target.value)}
              sx={{ mt: 2 }}
            />
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <Save />}
            sx={{ background: 'linear-gradient(90deg, #2A2E5B 0%, #5F4B8B 100%)', borderRadius: '24px', fontWeight: 700, textTransform: 'none' }}
          >
            {loading ? 'Submitting...' : 'Submit Report'}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default ReportForm; 